import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';
import { normalizeAuthorization, normalizeSignoff } from '@/lib/normalizeJob';
import type {
  AssignJobAppointmentDto,
  ConfirmJobAppointmentDto,
  CreateJobAppointmentDto,
  CreateJobAppointmentResponse,
  JobAppointment,
  SkipJobAppointmentDto,
  TransitionJobAppointmentDto,
  UpdateJobAppointmentDto,
} from '@/types/jobAppointment';

/**
 * Normalize an appointment from the API so the FE work-auth / sign-off card sees
 * a `collectedAt` alias (the backend persists `authorizedAt` / `signedAt`). Reuses
 * the same helpers as `normalizeJob` so job- and appointment-level cards behave
 * identically.
 */
function normalizeAppointment(raw: JobAppointment): JobAppointment {
  return {
    ...raw,
    authorization: normalizeAuthorization(raw.authorization),
    signoff: normalizeSignoff(raw.signoff),
  };
}

async function fetchAppointments(jobId: string): Promise<JobAppointment[]> {
  const list = await apiGet<JobAppointment[]>(operationsClient, jobEndpoints.appointments(jobId));
  return list.map(normalizeAppointment);
}

const PENDING_CONFIRMATION_REFRESH_MS = 10_000;
/**
 * Slower tier for visits a customer COULD still act on but has not yet. This is
 * a fallback behind the notification stream (see
 * `customerVisitEventInvalidations`), not the primary path, so it trades
 * latency for request volume: worst case the badge appears 30s late with the
 * stream down, and instantly with it up.
 */
const CUSTOMER_ACTIONABLE_REFRESH_MS = 30_000;

/**
 * Lifecycle statuses in which a customer-facing flow can still change a visit.
 * The public doors gate on exactly these (`MANAGEABLE` +
 * `appointmentIsManageable` in `ScheduleRequestPublicService`), so a visit that
 * is dispatched, in progress or finished is not reachable from a customer link.
 */
const CUSTOMER_CHANGEABLE_STATUSES = new Set(['RESERVED', 'SCHEDULED', 'CONFIRMED']);

/**
 * True while a customer-facing flow could still flip this visit under the
 * operator — an upcoming visit that is not yet out for work. A past visit is
 * excluded: the cutoff checks on every public door would refuse it anyway.
 */
function isCustomerActionable(appointment: JobAppointment, now: number): boolean {
  if (!CUSTOMER_CHANGEABLE_STATUSES.has(appointment.status)) return false;
  // Day-bound visits have no start until the optimizer assigns one; they are
  // upcoming by definition, so they stay in scope.
  if (!appointment.startAt) return true;
  return new Date(appointment.startAt).getTime() > now;
}

/** Refresh pending confirmations and active field work from other devices.
 * React Query pauses intervals while the operator's tab is in the background.
 */
export function pendingConfirmationRefetchInterval(
  query: {
    state: { data?: JobAppointment | JobAppointment[] };
  },
  now: number = Date.now(),
): number | false {
  const appointments = Array.isArray(query.state.data)
    ? query.state.data
    : query.state.data
      ? [query.state.data]
      : [];
  if (
    appointments.some(
      (appointment) =>
        appointment.confirmationStatus === 'PENDING' ||
        ['DISPATCHED', 'IN_PROGRESS', 'ON_HOLD'].includes(appointment.status) ||
        (CUSTOMER_CHANGEABLE_STATUSES.has(appointment.status) &&
          Boolean(appointment.startAt) &&
          new Date(appointment.startAt!).getTime() <= now),
    )
  ) {
    return PENDING_CONFIRMATION_REFRESH_MS;
  }
  return appointments.some((appointment) => isCustomerActionable(appointment, now))
    ? CUSTOMER_ACTIONABLE_REFRESH_MS
    : false;
}

/**
 * Outbox event types behind the staff notifications a CUSTOMER causes on a
 * visit. When one of these arrives on the notification stream the operator's
 * cached job queries are stale by definition, so they are invalidated
 * immediately rather than waiting for the poll above.
 */
export const CUSTOMER_DRIVEN_VISIT_EVENT_TYPES: ReadonlySet<string> = new Set([
  'appointment.reschedule_requested',
  'appointment.confirmed',
  'appointment.cancelled_by_customer',
]);

/**
 * The job queries to refresh when such a notification lands. The notification
 * carries the APPOINTMENT id, not the job id, so this matches every cached
 * job's visit list rather than trying to resolve the owner — invalidation only
 * refetches ACTIVE queries, which is the job page the operator is looking at.
 */
export function customerVisitEventInvalidations(
  queryClient: ReturnType<typeof useQueryClient>,
  notificationType: string | null | undefined,
): boolean {
  if (!notificationType || !CUSTOMER_DRIVEN_VISIT_EVENT_TYPES.has(notificationType)) {
    return false;
  }
  const details = jobKeys.details();
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!Array.isArray(key) || key.length <= details.length) return false;
      if (details.some((part, index) => key[index] !== part)) return false;
      // `[...details, jobId, 'appointments', ...]` and the activity timeline —
      // the two places the request shows. Everything else on the job detail
      // subtree (finances, media, payments) is untouched by a customer reply.
      return key[details.length + 1] === 'appointments' || key[details.length + 1] === 'activity';
    },
  });
  return true;
}

export function useJobAppointments(jobId: string) {
  return useApiQuery<JobAppointment[]>({
    queryKey: jobKeys.appointments(jobId),
    queryFn: () => fetchAppointments(jobId),
    enabled: Boolean(jobId),
    refetchInterval: pendingConfirmationRefetchInterval,
  });
}

export function useJobAppointment(jobId: string, appointmentId: string) {
  const queryClient = useQueryClient();
  return useQuery<JobAppointment, Error>({
    queryKey: jobKeys.appointment(jobId, appointmentId),
    queryFn: async () => {
      const list = await fetchAppointments(jobId);
      const found = list.find((a) => a.id === appointmentId);
      if (!found) throw new Error('Visit not found');
      return found;
    },
    placeholderData: () => {
      const cached = queryClient.getQueryData<JobAppointment[]>(jobKeys.appointments(jobId));
      return cached?.find((a) => a.id === appointmentId);
    },
    enabled: Boolean(jobId) && Boolean(appointmentId),
    refetchInterval: pendingConfirmationRefetchInterval,
  });
}

export function useCreateJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<CreateJobAppointmentResponse, CreateJobAppointmentDto>({
    mutationFn: (dto) => apiPost(operationsClient, jobEndpoints.createAppointment(jobId), dto),
    // Errors are the caller's: every host routes the 409 conflict handshake
    // through useConflictOverride ("Schedule anyway?") and toasts the rest itself.
    showErrorToast: false,
    onSuccess: (_appointment, variables) => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(jobId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      toast.success(
        variables.scheduleMode === 'unscheduled'
          ? 'Visit created for Schedule Review'
          : 'Visit scheduled',
      );
    },
  });
}

function invalidateJobAppointmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
) {
  void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(jobId) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
}

export function useUpdateJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, UpdateJobAppointmentDto>({
    mutationFn: ({ appointmentId, ...dto }) =>
      apiPut<JobAppointment>(operationsClient, jobEndpoints.appointment(jobId, appointmentId), dto),
    onSuccess: () => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      toast.success('Visit updated');
    },
  });
}

/**
 * Stretch a visit's end so it fits work added to its job. Only the end moves:
 * the arrival window the customer holds is unchanged, so they are not told.
 * Errors are the caller's to show — a clash with the technician's next visit is
 * answered by opening the visit, which a generic toast cannot offer.
 */
export function useExtendJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, { appointmentId: string; startAt: string; endAt: string }>({
    showErrorToast: false,
    mutationFn: ({ appointmentId, startAt, endAt }) =>
      apiPut<JobAppointment>(operationsClient, jobEndpoints.appointment(jobId, appointmentId), {
        startAt,
        endAt,
        notifyCustomer: false,
      }),
    onSuccess: (_updated, { appointmentId }) => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      void queryClient.invalidateQueries({ queryKey: jobKeys.appointment(jobId, appointmentId) });
    },
  });
}

export function useTransitionJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, TransitionJobAppointmentDto>({
    mutationFn: ({ appointmentId, ...dto }) =>
      apiPost<JobAppointment>(
        operationsClient,
        jobEndpoints.appointmentTransition(jobId, appointmentId),
        dto,
      ),
    onSuccess: (_data, variables) => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      toast.success(`Visit moved to ${variables.status.replace(/_/g, ' ').toLowerCase()}`);
    },
  });
}

export function useAssignJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, AssignJobAppointmentDto>({
    mutationFn: ({ appointmentId, ...dto }) =>
      apiPost<JobAppointment>(
        operationsClient,
        jobEndpoints.appointmentAssign(jobId, appointmentId),
        dto,
      ),
    onSuccess: () => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      toast.success('Visit crew updated');
    },
  });
}

export function useConfirmJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, ConfirmJobAppointmentDto>({
    mutationFn: ({ appointmentId, ...dto }) =>
      apiPost<JobAppointment>(
        operationsClient,
        jobEndpoints.appointmentConfirm(jobId, appointmentId),
        dto,
      ),
    onSuccess: () => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      toast.success('Visit confirmed');
    },
  });
}

export function useSkipJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, SkipJobAppointmentDto>({
    mutationFn: ({ appointmentId, ...dto }) =>
      apiPost<JobAppointment>(
        operationsClient,
        jobEndpoints.appointmentSkip(jobId, appointmentId),
        dto,
      ),
    onSuccess: (_visit, { noShow }) => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      toast.success(noShow ? 'Marked as no-show' : 'Visit skipped');
    },
  });
}

export function useCancelJobAppointment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobAppointment, { appointmentId: string }>({
    mutationFn: ({ appointmentId }) =>
      apiDelete<JobAppointment>(operationsClient, jobEndpoints.appointment(jobId, appointmentId)),
    onSuccess: () => {
      invalidateJobAppointmentQueries(queryClient, jobId);
      toast.success('Visit cancelled');
    },
  });
}
